import type { Coordinates, GeoLocation } from '../types';

// Nominatim API для обратного геокодирования (координаты -> адрес)
const NOMINATIM_API_BASE = 'https://nominatim.openstreetmap.org';

export interface NominatimResponse {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city_district?: string;
    city?: string;
    municipality?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: string[];
}

export interface SearchResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: string[];
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
}

// Обратное геокодирование: координаты -> адрес
export const reverseGeocode = async (coordinates: Coordinates): Promise<GeoLocation> => {
  try {
    const response = await fetch(
      `${NOMINATIM_API_BASE}/reverse?` +
      `lat=${coordinates.latitude}&` +
      `lon=${coordinates.longitude}&` +
      `format=json&` +
      `addressdetails=1&` +
      `accept-language=ru,en`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: NominatimResponse = await response.json();

    // Извлечение компонентов адреса
    const address = data.address || {};
    
    // Определение города из различных полей
    const city = address.city || 
                 address.municipality || 
                 address.city_district || 
                 address.suburb ||
                 address.neighbourhood ||
                 'Неизвестный город';

    // Формирование полного адреса
    const addressParts = [];
    
    if (address.house_number && address.road) {
      addressParts.push(`${address.road}, ${address.house_number}`);
    } else if (address.road) {
      addressParts.push(address.road);
    }
    
    if (address.neighbourhood && address.neighbourhood !== city) {
      addressParts.push(address.neighbourhood);
    }
    
    addressParts.push(city);
    
    if (address.state && address.state !== city) {
      addressParts.push(address.state);
    }
    
    if (address.country) {
      addressParts.push(address.country);
    }

    const full_address = addressParts.length > 0 ? addressParts.join(', ') : data.display_name;

    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      country: address.country || 'Неизвестная страна',
      city: city,
      full_address: full_address,
      location_id: data.place_id?.toString(),
    };
  } catch (error) {
    console.error('Ошибка обратного геокодирования:', error);
    
    // Возвращаем базовую информацию в случае ошибки
    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      country: undefined,
      city: undefined,
      full_address: `${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}`,
      location_id: undefined,
    };
  }
};

// Прямое геокодирование: адрес -> координаты
export const searchPlaces = async (query: string): Promise<SearchResult[]> => {
  try {
    const response = await fetch(
      `${NOMINATIM_API_BASE}/search?` +
      `q=${encodeURIComponent(query)}&` +
      `format=json&` +
      `addressdetails=1&` +
      `limit=10&` +
      `accept-language=ru,en`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: SearchResult[] = await response.json();
    return data;
  } catch (error) {
    console.error('Ошибка поиска мест:', error);
    return [];
  }
};

// Получение координат по названию места
export const getCoordinatesByPlaceName = async (placeName: string): Promise<Coordinates | null> => {
  try {
    const results = await searchPlaces(placeName);
    
    if (results.length === 0) {
      return null;
    }

    const firstResult = results[0];
    return {
      latitude: parseFloat(firstResult.lat),
      longitude: parseFloat(firstResult.lon),
    };
  } catch (error) {
    console.error('Ошибка получения координат:', error);
    return null;
  }
};

// Проверка доступности геокодирования
export const isGeocodingAvailable = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${NOMINATIM_API_BASE}/status`, { 
      method: 'HEAD',
      timeout: 5000 
    } as any);
    return response.ok;
  } catch (error) {
    console.warn('Геокодирование недоступно:', error);
    return false;
  }
};

// Популярные места (fallback когда геокодирование недоступно)
export const POPULAR_LOCATIONS = [
  { name: 'Москва, Россия', latitude: 55.7558, longitude: 37.6176, country: 'Россия', city: 'Москва' },
  { name: 'Санкт-Петербург, Россия', latitude: 59.9311, longitude: 30.3609, country: 'Россия', city: 'Санкт-Петербург' },
  { name: 'Новосибирск, Россия', latitude: 55.0084, longitude: 82.9357, country: 'Россия', city: 'Новосибирск' },
  { name: 'Екатеринбург, Россия', latitude: 56.8431, longitude: 60.6454, country: 'Россия', city: 'Екатеринбург' },
  { name: 'Казань, Россия', latitude: 55.8304, longitude: 49.0661, country: 'Россия', city: 'Казань' },
  { name: 'Нижний Новгород, Россия', latitude: 56.2965, longitude: 43.9361, country: 'Россия', city: 'Нижний Новгород' },
  { name: 'Челябинск, Россия', latitude: 55.1644, longitude: 61.4368, country: 'Россия', city: 'Челябинск' },
  { name: 'Омск, Россия', latitude: 54.9885, longitude: 73.3242, country: 'Россия', city: 'Омск' },
  { name: 'Самара, Россия', latitude: 53.2001, longitude: 50.1500, country: 'Россия', city: 'Самара' },
  { name: 'Ростов-на-Дону, Россия', latitude: 47.2357, longitude: 39.7015, country: 'Россия', city: 'Ростов-на-Дону' },
  { name: 'Лондон, Великобритания', latitude: 51.5074, longitude: -0.1278, country: 'Великобритания', city: 'Лондон' },
  { name: 'Париж, Франция', latitude: 48.8566, longitude: 2.3522, country: 'Франция', city: 'Париж' },
  { name: 'Берлин, Германия', latitude: 52.5200, longitude: 13.4050, country: 'Германия', city: 'Берлин' },
  { name: 'Нью-Йорк, США', latitude: 40.7128, longitude: -74.0060, country: 'США', city: 'Нью-Йорк' },
  { name: 'Токио, Япония', latitude: 35.6762, longitude: 139.6503, country: 'Япония', city: 'Токио' },
];

// Fallback геокодирование с использованием популярных мест
export const fallbackGeocode = async (coordinates: Coordinates): Promise<GeoLocation> => {
  // Поиск ближайшего популярного места
  let bestMatch = POPULAR_LOCATIONS[0];
  let minDistance = Infinity;

  POPULAR_LOCATIONS.forEach(location => {
    const distance = Math.sqrt(
      Math.pow(location.latitude - coordinates.latitude, 2) +
      Math.pow(location.longitude - coordinates.longitude, 2)
    );
    if (distance < minDistance) {
      minDistance = distance;
      bestMatch = location;
    }
  });

  // Если ближайшее место далеко (>2 градусов), используем общие названия
  if (minDistance > 2) {
    let country = 'Неизвестная страна';
    let city = 'Неизвестный город';
    
    // Простое определение региона
    if (coordinates.latitude >= 41 && coordinates.latitude <= 82 && 
        coordinates.longitude >= 19 && coordinates.longitude <= 180) {
      country = 'Россия';
    } else if (coordinates.latitude >= 35 && coordinates.latitude <= 71 && 
               coordinates.longitude >= -10 && coordinates.longitude <= 40) {
      country = 'Европа';
    } else if (coordinates.latitude >= 25 && coordinates.latitude <= 49 && 
               coordinates.longitude >= -125 && coordinates.longitude <= -66) {
      country = 'США';
    }

    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      country,
      city,
      full_address: `${city}, ${country}`,
      location_id: undefined,
    };
  }

  // Используем ближайшее популярное место
  return {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    country: bestMatch.country,
    city: bestMatch.city,
    full_address: `${bestMatch.city}, ${bestMatch.country}`,
    location_id: undefined,
  };
}; 